
import React from 'react';
import { Button } from "@/components/ui/button";
import { Plus, Trash2, LayoutList, ArrowUp, ArrowDown } from "lucide-react";
import { Label } from '@/components/ui/label';

interface WindowNodeConfigProps {
    data: any;
    onChange: (field: string, value: any) => void;
    inputColumns: any[];
}

const WINDOW_FUNCTIONS = [
    { value: 'ROW_NUMBER', label: 'Row Number' },
    { value: 'RANK', label: 'Rank' },
    { value: 'DENSE_RANK', label: 'Dense Rank' },
    { value: 'AVG', label: 'Running/Rolling Average' },
    { value: 'SUM', label: 'Running/Rolling Sum' },
    { value: 'MIN', label: 'Running Min' },
    { value: 'MAX', label: 'Running Max' },
    { value: 'LEAD', label: 'Lead (Next Row)' },
    { value: 'LAG', label: 'Lag (Previous Row)' },
    { value: 'FIRST_VALUE', label: 'First Value' },
    { value: 'LAST_VALUE', label: 'Last Value' },
    { value: 'NTILE', label: 'NTiles (Buckets)' },
];

export const WindowNodeConfig = ({ data, onChange, inputColumns = [] }: WindowNodeConfigProps) => {

    // -- State Accessors --
    const functions = data.windowFunctions || [];
    const partitions = data.partitionBy || [];
    const orders = data.orderBy || []; // Array of { column, direction }

    // -- Helpers --

    const updateFunction = (idx: number, field: string, value: any) => {
        const newFuncs = [...functions];
        // Ensure object exists
        if (!newFuncs[idx]) newFuncs[idx] = {};

        newFuncs[idx] = { ...newFuncs[idx], [field]: value };

        // Auto-generate Output Alias if not set
        if (field === 'function' || field === 'targetColumn') {
            const f = newFuncs[idx];
            // If alias is empty, try to generate one: e.g. "Rank_Date"
            if (!f.alias) {
                const colSuffix = f.targetColumn ? `_${f.targetColumn}` : '';
                f.alias = `${f.function}${colSuffix}`;
            }
        }

        onChange('windowFunctions', newFuncs);
    };

    const addFunction = () => {
        const newFunc = {
            function: 'ROW_NUMBER',
            targetColumn: '', // Input Column (for aggs)
            alias: '', // Output Name
            param1: null, // Offset or Buckets
            param2: null // Default value
        };
        onChange('windowFunctions', [...functions, newFunc]);
    };

    const removeFunction = (idx: number) => {
        const newFuncs = [...functions];
        newFuncs.splice(idx, 1);
        onChange('windowFunctions', newFuncs);
    };

    // -- Partition Helpers --
    const togglePartition = (colName: string, checked: boolean) => {
        let newParts = [...partitions];
        if (checked) {
            if (!newParts.includes(colName)) newParts.push(colName);
        } else {
            newParts = newParts.filter((p: string) => p !== colName);
        }
        onChange('partitionBy', newParts);
    };

    // -- Order Helpers --
    const toggleOrder = (colName: string, checked: boolean) => {
        let newOrders = [...orders];
        const existingIdx = newOrders.findIndex((o: any) => o.column === colName);

        if (checked) {
            if (existingIdx === -1) {
                newOrders.push({ column: colName, direction: 'ASC' });
            }
        } else {
            if (existingIdx !== -1) {
                newOrders.splice(existingIdx, 1);
            }
        }
        onChange('orderBy', newOrders);
    };

    const updateOrderDirection = (colName: string, direction: 'ASC' | 'DESC') => {
        const newOrders = orders.map((o: any) => {
            if (o.column === colName) return { ...o, direction };
            return o;
        });
        onChange('orderBy', newOrders);
    };


    return (
        <div className="space-y-6">
            <h3 className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
                <LayoutList className="h-3 w-3 text-violet-500" />
                Window Configuration
            </h3>

            {/* 1. Partition By */}
            <div className="space-y-2">
                <Label className="text-xs text-gray-500 font-medium uppercase">Partition By (Group)</Label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1">
                    {inputColumns.map(col => {
                        const isChecked = partitions.includes(col.name);
                        return (
                            <label key={col.name} className="flex items-center gap-2 p-1 hover:bg-gray-100 rounded cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                                    checked={isChecked}
                                    onChange={(e) => togglePartition(col.name, e.target.checked)}
                                />
                                <span className="text-xs text-gray-700 truncate">{col.name}</span>
                                <span className="text-[9px] text-gray-400 ml-auto">{col.type}</span>
                            </label>
                        );
                    })}
                    {inputColumns.length === 0 && <div className="text-xs text-gray-400 italic p-1">No input columns available.</div>}
                </div>
                <p className="text-[10px] text-gray-400">Rows will be grouped by these columns before calculation.</p>
            </div>

            {/* 2. Order By */}
            <div className="space-y-2">
                <Label className="text-xs text-gray-500 font-medium uppercase">Order By (Sort)</Label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1">
                    {inputColumns.map(col => {
                        const orderItem = orders.find((o: any) => o.column === col.name);
                        const isChecked = !!orderItem;

                        return (
                            <div key={col.name} className={`flex items-center gap-2 p-1 rounded ${isChecked ? 'bg-violet-50' : 'hover:bg-gray-100'}`}>
                                <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                                        checked={isChecked}
                                        onChange={(e) => toggleOrder(col.name, e.target.checked)}
                                    />
                                    <span className="text-xs text-gray-700 truncate">{col.name}</span>
                                </label>

                                {isChecked && (
                                    <div className="flex bg-white rounded border border-gray-200 shadow-sm">
                                        <button
                                            title="Ascending"
                                            className={`p-1 rounded-l ${orderItem.direction === 'ASC' ? 'text-violet-600 bg-violet-100' : 'text-gray-400 hover:bg-gray-50'}`}
                                            onClick={() => updateOrderDirection(col.name, 'ASC')}
                                        >
                                            <ArrowUp className="h-3 w-3" />
                                        </button>
                                        <button
                                            title="Descending"
                                            className={`p-1 rounded-r border-l ${orderItem.direction === 'DESC' ? 'text-violet-600 bg-violet-100' : 'text-gray-400 hover:bg-gray-50'}`}
                                            onClick={() => updateOrderDirection(col.name, 'DESC')}
                                        >
                                            <ArrowDown className="h-3 w-3" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {inputColumns.length === 0 && <div className="text-xs text-gray-400 italic p-1">No input columns available.</div>}
                </div>
                <p className="text-[10px] text-gray-400">Order determines rank or running total sequence.</p>
            </div>

            {/* 3. Window Functions */}
            <div className="space-y-3 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                    <Label className="text-xs text-gray-500 font-medium uppercase">Calculated Columns</Label>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={addFunction}>
                        <Plus className="h-3 w-3" /> Add Function
                    </Button>
                </div>

                <div className="space-y-3">
                    {functions.map((func: any, i: number) => (
                        <div key={i} className="bg-white border rounded-lg p-3 space-y-3 shadow-sm group hover:border-violet-200 transition-colors">
                            <div className="flex justify-between items-center border-b pb-2 border-gray-50">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Column #{i + 1}</span>
                                <button onClick={() => removeFunction(i)} className="text-gray-300 hover:text-red-500 transition-colors">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                {/* Function Selection */}
                                <div className="col-span-2 space-y-1">
                                    <Label className="text-[10px] text-gray-500">Function</Label>
                                    <select
                                        className="w-full h-8 rounded border border-gray-200 text-xs bg-gray-50 focus:bg-white focus:ring-1 focus:ring-violet-500 transition-all"
                                        value={func.function}
                                        onChange={(e) => updateFunction(i, 'function', e.target.value)}
                                    >
                                        {WINDOW_FUNCTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                    </select>
                                </div>

                                {/* Target Column (Input) - Conditionally Shown */}
                                {['SUM', 'AVG', 'MIN', 'MAX', 'LEAD', 'LAG', 'FIRST_VALUE', 'LAST_VALUE'].includes(func.function) && (
                                    <div className="col-span-2 sm:col-span-1 space-y-1">
                                        <Label className="text-[10px] text-gray-500">Input Column</Label>
                                        <select
                                            className="w-full h-8 rounded border border-gray-200 text-xs bg-white"
                                            value={func.targetColumn || ''}
                                            onChange={(e) => updateFunction(i, 'targetColumn', e.target.value)}
                                        >
                                            <option value="">Select Column...</option>
                                            {inputColumns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                        </select>
                                    </div>
                                )}

                                {/* Alias (Output Name) */}
                                <div className={['SUM', 'AVG', 'MIN', 'MAX', 'LEAD', 'LAG', 'FIRST_VALUE', 'LAST_VALUE'].includes(func.function) ? "col-span-2 sm:col-span-1 space-y-1" : "col-span-2 space-y-1"}>
                                    <Label className="text-[10px] text-gray-500">Output Column Name</Label>
                                    <input
                                        className="w-full h-8 px-2 rounded border border-gray-200 text-xs focus:ring-1 focus:ring-violet-500 placeholder:text-gray-300"
                                        value={func.alias || ''}
                                        onChange={(e) => updateFunction(i, 'alias', e.target.value)}
                                        placeholder="e.g. Rank_Val"
                                    />
                                </div>

                                {/* Extra Params for NTILE */}
                                {func.function === 'NTILE' && (
                                    <div className="col-span-2 space-y-1">
                                        <Label className="text-[10px] text-gray-500">Number of Buckets</Label>
                                        <input
                                            type="number"
                                            className="w-full h-8 px-2 border rounded text-xs"
                                            value={func.param1 || 4}
                                            onChange={(e) => updateFunction(i, 'param1', parseInt(e.target.value))}
                                        />
                                    </div>
                                )}

                                {/* Extra Params for LEAD/LAG */}
                                {['LEAD', 'LAG'].includes(func.function) && (
                                    <>
                                        <div className="space-y-1">
                                            <Label className="text-[10px] text-gray-500">Offset (Rows)</Label>
                                            <input
                                                type="number"
                                                className="w-full h-8 px-2 border rounded text-xs"
                                                value={func.param1 || 1}
                                                onChange={(e) => updateFunction(i, 'param1', parseInt(e.target.value))}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-[10px] text-gray-500">Default Value</Label>
                                            <input
                                                className="w-full h-8 px-2 border rounded text-xs"
                                                value={func.param2 || ''}
                                                onChange={(e) => updateFunction(i, 'param2', e.target.value)}
                                                placeholder="NULL"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}

                    {functions.length === 0 && (
                        <div className="text-center p-6 border-2 border-dashed border-gray-100 rounded-lg bg-gray-50/50">
                            <p className="text-xs text-gray-400 mb-2">No window functions configured.</p>
                            <Button size="sm" variant="ghost" className="text-violet-600 h-7 text-[10px]" onClick={addFunction}>
                                + Add First Function
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
